import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  nombre_fiscal: '', nombre_comercial: '', cif_nif: '',
  direccion: '', codigo_postal: '', poblacion: '', provincia: '',
  pais: 'España', codigo_pais: 'ES',
  email: '', telefono: '', persona_contacto: '', web: '',
  iban: '', forma_pago: 'Transferencia', dias_vencimiento: 30,
  categoria_gasto: '', notas: '', activo: true
};

const Suppliers = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const canEdit = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/billing/suppliers`);
      setItems(res.data);
    } catch (e) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c }); setShowDialog(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.dias_vencimiento = parseInt(payload.dias_vencimiento || 30);
      if (editing) {
        await axios.put(`${API_URL}/billing/suppliers/${editing.id}`, payload);
        toast.success('Proveedor actualizado');
      } else {
        await axios.post(`${API_URL}/billing/suppliers`, payload);
        toast.success('Proveedor creado');
      }
      setShowDialog(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try {
      await axios.delete(`${API_URL}/billing/suppliers/${id}`);
      toast.success('Proveedor eliminado');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtered = items.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.nombre_fiscal?.toLowerCase().includes(s) ||
           c.nombre_comercial?.toLowerCase().includes(s) ||
           c.cif_nif?.toLowerCase().includes(s) ||
           c.email?.toLowerCase().includes(s);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="suppliers-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Proveedores</h1>
            <p className="text-slate-500 mt-1">Empresas y autónomos a los que compras bienes o servicios</p>
          </div>
          {canEdit && (
            <Button data-testid="new-supplier-button" onClick={openCreate} className="bg-[#004E89] hover:bg-[#003a68]">
              <Plus className="w-4 h-4 mr-2" /> Nuevo proveedor
            </Button>
          )}
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="suppliers-search" className="pl-9" placeholder="Buscar por nombre, CIF, email..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              {search ? 'No se encontraron proveedores.' : 'Todavía no tienes proveedores registrados.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Nombre fiscal</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">CIF/NIF</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Categoría</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Email</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Estado</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-sm font-medium text-slate-900">
                        {c.nombre_fiscal}
                        {c.nombre_comercial && <div className="text-xs text-slate-500">{c.nombre_comercial}</div>}
                      </td>
                      <td className="p-3 text-sm text-slate-700 font-mono">{c.cif_nif}</td>
                      <td className="p-3 text-sm text-slate-600">{c.categoria_gasto || '-'}</td>
                      <td className="p-3 text-sm text-slate-600">{c.email || '-'}</td>
                      <td className="p-3">
                        <span className={`inline-block text-xs px-2 py-1 rounded-full ${c.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {c.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-2">
                        {canEdit && (
                          <>
                            <button onClick={()=>openEdit(c)} className="text-slate-600 hover:text-blue-600"><Edit className="w-4 h-4 inline" /></button>
                            <button onClick={()=>handleDelete(c.id)} className="text-slate-600 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Datos fiscales</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Nombre fiscal *</Label>
                    <Input required value={form.nombre_fiscal} onChange={e=>set('nombre_fiscal', e.target.value)} />
                  </div>
                  <div>
                    <Label>Nombre comercial</Label>
                    <Input value={form.nombre_comercial} onChange={e=>set('nombre_comercial', e.target.value)} />
                  </div>
                  <div>
                    <Label>CIF/NIF *</Label>
                    <Input required value={form.cif_nif} onChange={e=>set('cif_nif', e.target.value)} />
                  </div>
                </div>
              </fieldset>

              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Dirección y contacto</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Dirección</Label>
                    <Input value={form.direccion} onChange={e=>set('direccion', e.target.value)} />
                  </div>
                  <div><Label>Código postal</Label><Input value={form.codigo_postal} onChange={e=>set('codigo_postal', e.target.value)} /></div>
                  <div><Label>Población</Label><Input value={form.poblacion} onChange={e=>set('poblacion', e.target.value)} /></div>
                  <div><Label>Provincia</Label><Input value={form.provincia} onChange={e=>set('provincia', e.target.value)} /></div>
                  <div><Label>País</Label><Input value={form.pais} onChange={e=>set('pais', e.target.value)} /></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>set('email', e.target.value)} /></div>
                  <div><Label>Teléfono</Label><Input value={form.telefono} onChange={e=>set('telefono', e.target.value)} /></div>
                  <div><Label>Persona de contacto</Label><Input value={form.persona_contacto} onChange={e=>set('persona_contacto', e.target.value)} /></div>
                  <div><Label>Web</Label><Input value={form.web} onChange={e=>set('web', e.target.value)} /></div>
                </div>
              </fieldset>

              <fieldset className="border border-slate-200 rounded-lg p-4">
                <legend className="text-sm font-semibold text-slate-700 px-2">Condiciones</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>IBAN</Label><Input value={form.iban} onChange={e=>set('iban', e.target.value)} /></div>
                  <div>
                    <Label>Forma de pago</Label>
                    <select className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" value={form.forma_pago} onChange={e=>set('forma_pago', e.target.value)}>
                      <option>Transferencia</option>
                      <option>Domiciliación</option>
                      <option>Efectivo</option>
                      <option>Tarjeta</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div><Label>Días de vencimiento</Label><Input type="number" value={form.dias_vencimiento} onChange={e=>set('dias_vencimiento', e.target.value)} /></div>
                  <div><Label>Categoría de gasto</Label><Input value={form.categoria_gasto} onChange={e=>set('categoria_gasto', e.target.value)} placeholder="p.ej. Material sanitario" /></div>
                </div>
              </fieldset>

              <div>
                <Label>Notas</Label>
                <textarea className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={2} value={form.notas} onChange={e=>set('notas', e.target.value)} />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={()=>setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#004E89] hover:bg-[#003a68]">{editing ? 'Guardar' : 'Crear'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Suppliers;
